/**
 * Every investor's MF records, one tab per list endpoint.
 *
 * Paging and the one-record id filter are the API's own (`page`, `page_size`
 * and the per-list id parameter). SIP modifications take no id; instead they
 * can be narrowed to one investor through `.../get/{user_uuid}/`.
 *
 * An admin without `can_see_all` sees only their own leads' records, as on the
 * Users and RFQ Orders pages. The APIs return every investor's records, so the
 * rule is applied per page here: a record is shown when the user directory
 * says its investor is the admin's lead. A record whose investor cannot be
 * matched is not shown to them — the rule fails closed.
 *
 * SIP modification instructions carry no investor, only their SIP; the SIPs
 * named on each page are looked up (fetchMFSipsById) and the investor read from
 * them. Records are never rewritten — the details view shows exactly what the
 * API returned.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  DEFAULT_PAGE_SIZE,
  MF_LISTS,
  fetchMFList,
  fetchMFSipsById,
  investorUuidOf,
  recordIdOf,
} from "../../../Api/mfAdminOverview";
import type { MFListKind, MFListPage } from "../../../Api/mfAdminOverview";
import { getMFErrorMessage } from "../../../Api/mfAdminInvestments";
import type { MFRecord } from "../../../Api/mfAdminInvestments";
import { fetchMFPlanModifications } from "../../../Api/mfAdminPlanModification";
import { canSeeAllLeads, isVisibleLead } from "../../../Helpers/adminPermissions";
import { OVERVIEW_COLUMNS } from "./mfOverviewColumns";
import type { ColumnContext } from "./mfOverviewColumns";
import { formatCount, humanizeKey } from "./mfOverviewFormat";
import RecordDetailsDialog from "./RecordDetailsDialog";
import type { DirectoryInvestor, InvestorDirectory } from "./useOverviewLookups";
import styles from "./mf-overview.module.css";

const PAGE_SIZES = [25, 50, 100];

const MFRecordsSection = ({
  directory,
  schemeNames,
  onOpenInvestor,
}: {
  directory: InvestorDirectory;
  schemeNames: Record<string, string>;
  onOpenInvestor?: (investor: DirectoryInvestor) => void;
}) => {
  const [kind, setKind] = useState<MFListKind>("purchases");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [idDraft, setIdDraft] = useState("");
  const [appliedId, setAppliedId] = useState("");
  const [investorFilter, setInvestorFilter] = useState<DirectoryInvestor | null>(null);
  const [data, setData] = useState<MFListPage | null>(null);
  /** The SIPs a page of modification instructions names, by purchase plan id. */
  const [plans, setPlans] = useState<Record<string, MFRecord>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState<MFRecord | null>(null);
  const [sort, setSort] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);
  const requestRef = useRef(0);

  const config = MF_LISTS.filter((item) => item.kind === kind)[0];
  const columns = OVERVIEW_COLUMNS[kind];
  const leadsOnly = useMemo(() => !canSeeAllLeads(), []);

  useEffect(() => {
    const request = ++requestRef.current;
    setLoading(true);
    setError("");
    const load = async () => {
      const listPage: MFListPage =
        kind === "modifications" && investorFilter
          ? await fetchMFPlanModifications(investorFilter.uuid).then((rows) => ({
              rows,
              count: rows.length,
            }))
          : await fetchMFList(config, { page, pageSize, id: appliedId });
      // Instructions name their SIP only; its record supplies the investor and
      // the fund. Rows are shown only once this is known, so a scoped admin's
      // filter never runs on half an answer.
      const planIds =
        kind === "modifications"
          ? listPage.rows.map((row) => String(row.plan || "")).filter(Boolean)
          : [];
      const foundPlans = planIds.length ? await fetchMFSipsById(planIds) : {};
      return { listPage, foundPlans };
    };
    load()
      .then(({ listPage, foundPlans }) => {
        if (request !== requestRef.current) return;
        setPlans(foundPlans);
        setData(listPage);
      })
      .catch((requestError) => {
        if (request !== requestRef.current) return;
        setData(null);
        setPlans({});
        setError(getMFErrorMessage(requestError, `${config.label} could not be loaded.`));
      })
      .finally(() => {
        if (request === requestRef.current) setLoading(false);
      });
  }, [config, kind, page, pageSize, appliedId, investorFilter, reloadKey]);

  useEffect(
    () => () => {
      requestRef.current += 1;
    },
    []
  );

  const switchTab = (next: MFListKind) => {
    if (next === kind) return;
    setKind(next);
    setPage(1);
    setIdDraft("");
    setAppliedId("");
    setInvestorFilter(null);
    setData(null);
    setSort(null);
  };

  /* Ascending, then descending, then back to the order the API returned. */
  const toggleSort = (key: string) =>
    setSort((current) =>
      !current || current.key !== key
        ? { key, direction: "asc" }
        : current.direction === "asc"
        ? { key, direction: "desc" }
        : null
    );

  /**
   * Whose record this is: the investor filtered to (every row then is theirs),
   * the record's own `user`, or its SIP's.
   */
  const investorUuidFor = useCallback(
    (row: MFRecord): string => {
      if (investorFilter) return investorFilter.uuid;
      const own = investorUuidOf(row);
      if (own) return own;
      const sip = plans[String(row.plan || "")];
      return sip ? investorUuidOf(sip) : "";
    },
    [investorFilter, plans]
  );

  const rows = useMemo(() => (data ? data.rows : []), [data]);
  const visibleRows = useMemo(() => {
    // The investor filter only offers this admin's own leads.
    if (!leadsOnly || investorFilter) return rows;
    return rows.filter((row) => {
      const investor = directory.byUuid[investorUuidFor(row)];
      return Boolean(investor) && isVisibleLead(investor.leadOwner);
    });
  }, [rows, leadsOnly, investorFilter, directory.byUuid, investorUuidFor]);
  const hiddenCount = rows.length - visibleRows.length;

  const ctx: ColumnContext = {
    schemeNames,
    directory,
    investorUuidFor,
    plans,
    onOpenInvestor,
  };

  /*
   * The lists page on the server and take no sort parameter, so a sort orders
   * the records on this page. Rows with nothing to sort by stay at the bottom
   * whichever way it runs, and ties keep the API's order.
   */
  const sortedRows = useMemo(() => {
    const column = sort ? columns.filter((item) => item.key === sort.key)[0] : undefined;
    if (!sort || !column || !column.sortValue) return visibleRows;
    const valueOf = column.sortValue;
    const direction = sort.direction === "asc" ? 1 : -1;
    return visibleRows
      .map((row, index) => ({ row, index, value: valueOf(row, ctx) }))
      .sort((left, right) => {
        if (left.value === null && right.value === null) return left.index - right.index;
        if (left.value === null) return 1;
        if (right.value === null) return -1;
        const order =
          typeof left.value === "number" && typeof right.value === "number"
            ? left.value - right.value
            : String(left.value).localeCompare(String(right.value), "en-IN", {
                numeric: true,
                sensitivity: "base",
              });
        return order * direction || left.index - right.index;
      })
      .map((entry) => entry.row);
    // `ctx` is rebuilt every render; what it carries is listed instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleRows, sort, columns, schemeNames, directory.byUuid, investorUuidFor, plans]);

  // Without the directory a scoped admin's rows cannot be checked, so none show.
  const waitingForDirectory = leadsOnly && !investorFilter && directory.loading;
  const directoryBlocked = leadsOnly && !investorFilter && Boolean(directory.error);

  const count = data ? data.count : null;
  const paged = !investorFilter;
  const totalPages = count !== null ? Math.max(1, Math.ceil(count / pageSize)) : null;
  const hasNext = totalPages !== null ? page < totalPages : rows.length >= pageSize;

  const applyId = (event: React.FormEvent) => {
    event.preventDefault();
    setAppliedId(idDraft.trim());
    setPage(1);
  };
  const clearId = () => {
    setIdDraft("");
    setAppliedId("");
    setPage(1);
  };

  const openRow = (row: MFRecord) => setSelected(row);
  const selectedInvestor = selected ? directory.byUuid[investorUuidFor(selected)] : undefined;

  const emptyMessage = appliedId
    ? `No ${config.noun.toLowerCase()} matches ${appliedId}.`
    : investorFilter
    ? `${investorFilter.name} has no SIP modifications.`
    : leadsOnly && rows.length
    ? "None of the records on this page belong to your leads."
    : `No ${config.label.toLowerCase()} yet.`;

  return (
    <section className={`${styles.panel} ${styles.recordsPanel}`}>
      <nav className={styles.tabs} aria-label="MF record lists">
        {MF_LISTS.map((item) => (
          <button
            key={item.kind}
            type="button"
            className={`${styles.tab} ${item.kind === kind ? styles.activeTab : ""}`}
            aria-current={item.kind === kind ? "page" : undefined}
            onClick={() => switchTab(item.kind)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      <div className={styles.recordsBody}>
        <div className={styles.toolbar}>
          {config.idParam ? (
            <form className={styles.searchForm} onSubmit={applyId}>
              <span className={styles.searchField}>
                <SearchIcon className={styles.searchIcon} />
                <input
                  className={styles.searchInput}
                  value={idDraft}
                  placeholder={`Find by ${humanizeKey(config.idParam)}`}
                  onChange={(event) => setIdDraft(event.target.value)}
                />
                {appliedId ? (
                  <button type="button" className={styles.clearButton} onClick={clearId} aria-label="Clear the search">
                    <CloseIcon style={{ fontSize: 16 }} />
                  </button>
                ) : null}
              </span>
              <button type="submit" className={styles.primaryButton} disabled={!idDraft.trim()}>
                Find
              </button>
            </form>
          ) : (
            <Autocomplete
              size="small"
              className={styles.investorFilter}
              options={directory.visible}
              loading={directory.loading}
              value={investorFilter}
              onChange={(_, value) => {
                setInvestorFilter(value);
                setPage(1);
              }}
              getOptionLabel={(option) =>
                [option.name, option.mobile && `+91 ${option.mobile}`].filter(Boolean).join(" · ")
              }
              isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
              ListboxProps={{ style: { fontFamily: "var(--mf-font)", fontSize: 13 } }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Filter by investor" />
              )}
            />
          )}

          <div className={styles.toolbarEnd}>
            {leadsOnly ? <span className={styles.scopeNote}>Showing your leads only</span> : null}
            {paged ? (
              <label className={styles.pageSize}>
                Rows
                <select
                  value={pageSize}
                  onChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => setReloadKey((value) => value + 1)}
              disabled={loading}
              aria-label={`Refresh ${config.label.toLowerCase()}`}
              title="Refresh"
            >
              <RefreshIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        </div>

        {leadsOnly && hiddenCount > 0 && !loading ? (
          <p className={styles.hiddenNote}>
            {formatCount(hiddenCount)} of the {formatCount(rows.length)} records on this page belong to
            other admins&apos; leads and are not shown.
          </p>
        ) : null}

        {error ? (
          <div className={styles.noticeBad}>
            <span>{error}</span>
            <button type="button" className={styles.ghostButton} onClick={() => setReloadKey((value) => value + 1)}>
              Try again
            </button>
          </div>
        ) : directoryBlocked ? (
          <div className={styles.noticeBad}>
            <span>
              Your leads could not be checked because the user directory did not load, so no records
              are shown. {directory.error}
            </span>
            <button type="button" className={styles.ghostButton} onClick={directory.reload}>
              Try again
            </button>
          </div>
        ) : loading || waitingForDirectory ? (
          <div className={styles.tableSkeleton}>
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <div key={index} className={styles.skeletonRow} />
            ))}
          </div>
        ) : visibleRows.length ? (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {columns.map((column) => {
                    const active = sort && sort.key === column.key ? sort.direction : null;
                    return (
                      <th
                        key={column.key}
                        className={column.numeric ? styles.numeric : undefined}
                        aria-sort={
                          active === "asc"
                            ? "ascending"
                            : active === "desc"
                            ? "descending"
                            : column.sortValue
                            ? "none"
                            : undefined
                        }
                      >
                        {column.sortValue ? (
                          <button
                            type="button"
                            className={`${styles.sortButton} ${active ? styles.sortActive : ""}`}
                            onClick={() => toggleSort(column.key)}
                            title={
                              active === "asc"
                                ? "Sorted ascending — click for descending"
                                : active === "desc"
                                ? "Sorted descending — click to clear"
                                : `Sort by ${column.label.toLowerCase()}`
                            }
                          >
                            {column.label}
                            {active === "asc" ? (
                              <ArrowUpwardIcon style={{ fontSize: 14 }} />
                            ) : active === "desc" ? (
                              <ArrowDownwardIcon style={{ fontSize: 14 }} />
                            ) : (
                              <UnfoldMoreIcon className={styles.sortIdle} style={{ fontSize: 15 }} />
                            )}
                          </button>
                        ) : (
                          column.label
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row, index) => (
                  <tr
                    key={recordIdOf(config, row) || index}
                    className={styles.clickableRow}
                    tabIndex={0}
                    title="Show every field of this record"
                    onClick={() => openRow(row)}
                    onKeyDown={(event) => {
                      // Keys pressed on the investor link belong to the link.
                      if (event.target !== event.currentTarget) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openRow(row);
                      }
                    }}
                  >
                    {columns.map((column) => (
                      <td key={column.key} className={column.numeric ? styles.numeric : undefined}>
                        {column.render(row, ctx)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={styles.emptyPanel}>
            <p>{emptyMessage}</p>
          </div>
        )}

        {paged && !error && (page > 1 || hasNext) ? (
          <div className={styles.pagination}>
            <span>
              Page {formatCount(page)}
              {totalPages !== null ? ` of ${formatCount(totalPages)}` : ""}
              {count !== null ? ` · ${formatCount(count)} records` : ""}
              {sort ? <span className={styles.sortHint}> · Sorted within this page</span> : null}
            </span>
            <div>
              <button
                type="button"
                className={styles.ghostButton}
                disabled={page <= 1 || loading}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
              >
                <ChevronLeftIcon style={{ fontSize: 17 }} /> Previous
              </button>
              <button
                type="button"
                className={styles.ghostButton}
                disabled={!hasNext || loading}
                onClick={() => setPage((value) => value + 1)}
              >
                Next <ChevronRightIcon style={{ fontSize: 17 }} />
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <RecordDetailsDialog
        record={selected}
        title={config.noun}
        subtitle={[
          selected ? recordIdOf(config, selected) : "",
          selectedInvestor ? selectedInvestor.name : "",
        ]
          .filter(Boolean)
          .join(" · ")}
        onClose={() => setSelected(null)}
      />
    </section>
  );
};

export default MFRecordsSection;
